import * as React from 'react'
import clsx from 'clsx'
import { SxProps, sx as sxFunc } from '../sx'
import styles from './index.module.scss'

export interface SkeletonClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `variant="text"`. */
  text: string
  /** Styles applied to the root element if `variant="rectangular"`. */
  rectangular: string
  /** Styles applied to the root element if `variant="rounded"`. */
  rounded: string
  /** Styles applied to the root element if `variant="circular"`. */
  circular: string
  /** Styles applied to the root element if `animation="pulse"`. */
  pulse: string
  /** Styles applied to the root element if `animation="wave"`. */
  wave: string
  /** Styles applied when the component is passed children. */
  withChildren: string
  /** Styles applied when the component is passed children and no width. */
  fitContent: string
  /** Styles applied when the component is passed children and no height. */
  heightAuto: string
}

export interface SkeletonProps
  extends Partial<React.HTMLAttributes<HTMLSpanElement>> {
  /**
   * Component to render
   * @default 'span'
   */
  Component?: React.ElementType
  /**
   * The animation.
   * If `false` the animation effect is disabled.
   * @default 'wave'
   */
  animation?: 'wave' | 'pulse' | false
  /**
   * Optional children to infer width and height from.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<SkeletonClasses>
  /**
   * Height of the skeleton.
   * Useful when you don't want to adapt the skeleton to a text element but for instance a card.
   */
  height?: number | string
  /**
   * Width of the skeleton.
   * Useful when the skeleton is inside an inline element with no width of its own.
   */
  width?: number | string

  /**
   * The type of content that will be rendered.
   * @default 'text'
   */
  variant?: 'circular' | 'rectangular' | 'rounded' | 'text'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
}

/**
 * Skeleton - Display a placeholder preview of your content before the data gets loaded to reduce load-time frustration.
 *
 */
export const Skeleton = React.forwardRef<HTMLSpanElement, SkeletonProps>(
  (props, ref) => {
    const {
      Component = 'span',
      className,
      classes,
      variant = 'text',
      animation = 'wave',
      width,
      height,
      children,
      style,
      sx,
      ...other
    } = props
    const hasChildren = Boolean(React.Children.count(children))
    return (
      <Component
        ref={ref}
        className={clsx(
          [styles.root, classes?.root],
          variant === 'text' && [styles.text, classes?.text],
          variant === 'circular' && [
            sxFunc({ borderRadius: 'circle' }),
            classes?.circular,
          ],
          variant === 'rectangular' && [
            sxFunc({ borderRadius: 0 }),
            classes?.rectangular,
          ],
          variant === 'rounded' && [
            sxFunc({ borderRadius: 4 }),
            classes?.rounded,
          ],
          animation !== false && [
            styles['animation'],
            styles[animation],
            classes?.[animation],
          ],
          hasChildren && [styles['has-children'], classes?.withChildren],
          hasChildren && !width && styles['auto-width'],
          hasChildren &&
            !height && [styles['auto-height'], classes?.heightAuto],
          className,
          sxFunc(sx)
        )}
        style={{
          ...style,
          width: width && (typeof width === 'number' ? `${width}px` : width),
          height:
            height && (typeof height === 'number' ? `${height}px` : height),
        }}
        {...other}
      >
        {children}
      </Component>
    )
  }
)
Skeleton.displayName = 'Skeleton'
